import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, AlertTriangle } from 'lucide-react';

/**
 * `TypeToConfirmDialog` — irreversible-action gate.
 *
 * Operator must type the literal word `DELETE` before the destructive
 * button enables. Prevents the "I clicked the wrong row" class of
 * accidents without adding any extra clicks for the genuine case.
 *
 * Visual language matches the existing Conversations / Contacts
 * delete confirms: glass-blurred backdrop, red accent on the icon and
 * primary button, neutral cancel.
 *
 * Props:
 *   open                Whether the modal is visible.
 *   onCancel()          Close without deleting.
 *   onConfirm()         Fire the actual delete. Disabled until the
 *                       operator has typed `DELETE` (case-sensitive).
 *   title               Headline (e.g. "Delete conversation?").
 *   description         Subhead (1–2 short lines describing the wipe).
 *   confirmWord         Defaults to "DELETE". Operator must type this
 *                       exact string to enable the destructive button.
 *   destructiveLabel    Button label (e.g. "Delete conversation").
 *   isDarkMode          Inherit caller's theme.
 *   busy                Disables both buttons + shows a pending label
 *                       while the network request is in flight.
 *   testIdPrefix        Optional namespace for data-testids so multiple
 *                       dialogs on one page don't collide.
 */
export const TypeToConfirmDialog = ({
  open,
  onCancel,
  onConfirm,
  title = 'Are you sure?',
  description = 'This action cannot be undone.',
  confirmWord = 'DELETE',
  destructiveLabel = 'Delete',
  isDarkMode = true,
  busy = false,
  testIdPrefix = 'type-to-confirm',
}) => {
  const [typed, setTyped] = useState('');
  const inputRef = useRef(null);

  // Reset + focus whenever the modal opens. Without this the previous
  // session's typed value lingers and the operator gets a confusing
  // "Delete is already enabled" state on the next open.
  useEffect(() => {
    if (open) {
      setTyped('');
      // Defer focus until after the entrance transition completes —
      // otherwise framer-motion's transform applied to the parent
      // steals focus mid-animation and the OS cursor blinks elsewhere.
      const t = setTimeout(() => {
        try { inputRef.current?.focus(); } catch { /* best-effort */ }
      }, 80);
      return () => clearTimeout(t);
    }
  }, [open]);

  const enabled = !busy && typed === confirmWord;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && enabled) {
      e.preventDefault();
      onConfirm();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-white/60' : 'text-gray-600';
  const cardBorder = isDarkMode ? 'border-white/10' : 'border-gray-200';
  const cardBg = isDarkMode ? 'bg-[#13141a]' : 'bg-white';
  const inputBg = isDarkMode
    ? 'bg-black/40 border-white/10 text-white placeholder:text-white/30'
    : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 backdrop-blur-sm"
          onClick={onCancel}
          data-testid={`${testIdPrefix}-backdrop`}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 8 }}
            transition={{ type: 'spring', damping: 22, stiffness: 320 }}
            className={`${cardBg} border ${cardBorder} rounded-xl p-5 w-full max-w-md mx-4 shadow-2xl`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testIdPrefix}-modal`}
          >
            {/* Header */}
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
                <AlertTriangle size={16} className="text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${textPrimary} truncate`} data-testid={`${testIdPrefix}-title`}>
                  {title}
                </p>
                <p className={`text-xs ${textSecondary} mt-0.5 leading-relaxed`}>
                  {description}
                </p>
              </div>
            </div>

            {/* Typed-confirmation input */}
            <div className="mt-4">
              <label className={`block text-xs ${textSecondary} mb-1.5`}>
                Type <span className="font-mono font-semibold text-red-400">{confirmWord}</span> to confirm
              </label>
              <input
                ref={inputRef}
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={busy}
                spellCheck={false}
                autoComplete="off"
                placeholder={confirmWord}
                className={`w-full px-3 py-2 text-sm font-mono rounded-lg border ${inputBg} focus:outline-none focus:border-red-400/60 focus:ring-1 focus:ring-red-400/30 transition-colors`}
                data-testid={`${testIdPrefix}-input`}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 mt-5">
              <button
                onClick={onCancel}
                disabled={busy}
                className={`flex-1 text-xs py-2 rounded-lg border ${cardBorder} ${textSecondary} hover:${isDarkMode ? 'bg-white/[0.05]' : 'bg-gray-50'} transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
                data-testid={`${testIdPrefix}-cancel-btn`}
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={!enabled}
                className={`flex-1 text-xs py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-1.5 ${
                  enabled
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-red-500/20 text-red-300/40 cursor-not-allowed'
                }`}
                data-testid={`${testIdPrefix}-confirm-btn`}
              >
                <Trash2 size={12} /> {busy ? 'Deleting…' : destructiveLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TypeToConfirmDialog;
